import {
  YMapRecord,
  type GeneratedImageWithHistory,
  type GeneratedImageWithHistoryYMap,
  type LocationAsset,
  type LocationAssetYMap,
  type LocationPanoramaViewDict,
  type LocationPanoramaViewDictYMap,
} from '../../../../generated/nodeDataTypes'

export const PANORAMA_SOURCE_IMAGE_SLOT = 'panorama_source'
export const PANORAMA_DEFAULT_VIEW: Required<LocationPanoramaViewDict> = {
  yaw_degrees: 0,
  pitch_degrees: 0,
  fov_degrees: 75,
}
export const PANORAMA_MIN_PITCH_DEGREES = -89
export const PANORAMA_MAX_PITCH_DEGREES = 89
export const PANORAMA_MIN_FOV_DEGREES = 20
export const PANORAMA_MAX_FOV_DEGREES = 120
export type PanoramaExtractCount = 1 | 4 | 8

type PanoramaViewLike =
  | LocationPanoramaViewDict
  | LocationPanoramaViewDictYMap
  | undefined

type ImageSlotMap =
  | Record<string, GeneratedImageWithHistory | undefined>
  | YMapRecord<GeneratedImageWithHistoryYMap, GeneratedImageWithHistory>

function finiteOrDefault(value: number | undefined, fallback: number): number {
  return Number.isFinite(value) ? Number(value) : fallback
}

export function clampPanoramaPitch(value: number): number {
  return Math.min(PANORAMA_MAX_PITCH_DEGREES, Math.max(PANORAMA_MIN_PITCH_DEGREES, value))
}

export function clampPanoramaFov(value: number): number {
  return Math.min(PANORAMA_MAX_FOV_DEGREES, Math.max(PANORAMA_MIN_FOV_DEGREES, value))
}

export function wrapPanoramaYaw(value: number): number {
  if (!Number.isFinite(value)) return PANORAMA_DEFAULT_VIEW.yaw_degrees
  if (value === 180) return 180
  const wrapped = ((((value + 180) % 360) + 360) % 360) - 180
  return Object.is(wrapped, -0) ? 0 : wrapped
}

export function normalizePanoramaView(
  view: PanoramaViewLike,
): Required<LocationPanoramaViewDict> {
  return {
    yaw_degrees: wrapPanoramaYaw(
      finiteOrDefault(view?.yaw_degrees, PANORAMA_DEFAULT_VIEW.yaw_degrees),
    ),
    pitch_degrees: clampPanoramaPitch(
      finiteOrDefault(view?.pitch_degrees, PANORAMA_DEFAULT_VIEW.pitch_degrees),
    ),
    fov_degrees: clampPanoramaFov(
      finiteOrDefault(view?.fov_degrees, PANORAMA_DEFAULT_VIEW.fov_degrees),
    ),
  }
}

export function getLocationImageSlotPath(
  output: LocationAsset | LocationAssetYMap | undefined,
  slot: string,
): string | undefined {
  const images: ImageSlotMap | undefined = output?.images
  if (!images) return undefined

  const imageSlot =
    images instanceof YMapRecord
      ? images.get(slot)
      : images[slot]
  const history = imageSlot?.history ?? []
  const pickedIndex = imageSlot?.picked_index ?? 0
  return history[pickedIndex]?.result?.local_path
}
